import {
  CmsContentBannerSchema,
  type CmsContentBannerForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { Button, FormCheckbox, FormRoot, RHF, zodResolver } from "@devhop/ui";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const BANNER_IMAGE_KEY_PREFIX = "uploads/cms/banner";

const FormSchema = z.object({
  items: CmsContentBannerSchema,
});
type FormValues = z.infer<typeof FormSchema>;

type BannerFormProps = {
  content: CmsContentBannerForm | null;
  onSubmit: (content: CmsContentBannerForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

export function BannerForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: BannerFormProps) {
  const defaultValues: FormValues = {
    items:
      Array.isArray(content) && content.length > 0
        ? content.map((item) => ({ ...item, isActive: item.isActive ?? true }))
        : [],
  };

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema) as Resolver<FormValues>,
    defaultValues,
  });

  const { fields, append, remove, move } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });

  const watchItems = methods.watch("items") ?? [];
  const setValue = methods.setValue;
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [uploading, setUploading] = useState(false);

  const setPendingFile = useCallback((index: number, file: File | null) => {
    const key = String(index);
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[key] = file;
      else delete next[key];
      return next;
    });
  }, []);

  const clearPendingAndKey = useCallback(
    (index: number) => {
      setValue(`items.${index}.fileKey`, "", { shouldDirty: true });
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[String(index)];
        return next;
      });
    },
    [setValue],
  );

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
      setPendingFiles((prev) => {
        const next: Record<string, File> = {};
        for (const k of Object.keys(prev)) {
          const file = prev[k];
          if (!file) continue;
          const i = Number.parseInt(k, 10);
          if (Number.isNaN(i) || i < index) next[k] = file;
          else if (i > index) next[String(i - 1)] = file;
        }
        return next;
      });
    },
    [remove],
  );

  const swapPendingFiles = useCallback((i: number, j: number) => {
    setPendingFiles((prev) => {
      const a = prev[String(i)];
      const b = prev[String(j)];
      const next = { ...prev };
      if (a) next[String(j)] = a;
      else delete next[String(j)];
      if (b) next[String(i)] = b;
      else delete next[String(i)];
      return next;
    });
  }, []);

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index <= 0) return;
      move(index, index - 1);
      swapPendingFiles(index, index - 1);
    },
    [move, swapPendingFiles],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index >= fields.length - 1) return;
      move(index, index + 1);
      swapPendingFiles(index, index + 1);
    },
    [move, swapPendingFiles, fields.length],
  );

  const handleSubmit = async (vals: FormValues) => {
    const items = vals.items.map((item, i) => ({
      ...item,
      order: i,
      isActive: item.isActive ?? true,
    }));
    const toUpload = Object.keys(pendingFiles);
    if (toUpload.length > 0) {
      setUploading(true);
      try {
        for (const key of toUpload) {
          const index = Number.parseInt(key, 10);
          const file = pendingFiles[key];
          const item = items[index];
          if (!file || !item) continue;
          const uploadedKey = await uploadImageToKey(
            BANNER_IMAGE_KEY_PREFIX,
            file,
          );
          item.fileKey = uploadedKey;
        }
      } finally {
        setUploading(false);
      }
    }
    onSubmit(items);
  };

  return (
    <FormRoot<FormValues>
      methods={methods as UseFormReturn<FormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">ລາຍການ Banner</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({ fileKey: "", order: fields.length, isActive: true })
            }
          >
            <PlusIcon className="h-4 w-4" />
            ເພີ່ມ
          </Button>
        </div>
        {fields.length === 0 && (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີລາຍການ. ກົດ ເພີ່ມ ເພື່ອເພີ່ມ banner.
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="space-y-3 rounded-lg border bg-muted/30 p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm">Banner {index + 1}</span>
                <div className="flex items-center gap-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    aria-label="ຍ້າຍຂຶ້ນ"
                  >
                    <ChevronUpIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === fields.length - 1}
                    aria-label="ຍ້າຍລົງ"
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(index)}
                    aria-label="ລຶບ"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <ImageKeyUploadField
                keyPrefix={BANNER_IMAGE_KEY_PREFIX}
                label="ຮູບ (16:9)"
                aspectHint="ອັດສ່ວນ 16:9"
                aspectRatio="aspect-video"
                widthPx={1920}
                heightPx={1080}
                value={watchItems[index]?.fileKey ?? ""}
                onChange={(key) =>
                  setValue(`items.${index}.fileKey`, key, {
                    shouldDirty: true,
                  })
                }
                disabled={submitting}
                deferUpload
                pendingFile={pendingFiles[String(index)] ?? null}
                onFileSelect={(file) => setPendingFile(index, file)}
                onClear={() => clearPendingAndKey(index)}
              />
              <FormCheckbox name={`items.${index}.isActive`} label="ເປີດໃຊ້ງານ" />
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດຮູບ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
